import { ComponentRef, Directive, forwardRef, Injector, Input, OnChanges, reflectComponentType, SimpleChanges, Type, ViewContainerRef } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';


const isControlValueAccessor = <T>(obj: T | ControlValueAccessor): obj is ControlValueAccessor =>
    typeof (obj as ControlValueAccessor).writeValue === 'function';

type HasIsDisabledState = { setDisabledState(isDisabled: boolean): void };

const hasIsDisabledState = <T>(obj: T | HasIsDisabledState): obj is HasIsDisabledState =>
    typeof (obj as HasIsDisabledState).setDisabledState === 'function';

const isValidator = <T>(obj: T | Validator): obj is Validator =>
    typeof (obj as Validator).validate === 'function';


@Directive({
    selector: '[componentOutlet]',
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ComponentOutletDirective), multi: true },
        { provide: NG_VALIDATORS, useExisting: forwardRef(() => ComponentOutletDirective), multi: true },
    ]
})
export class ComponentOutletDirective<T extends object = object> implements OnChanges, ControlValueAccessor, Validator {

    @Input() componentOutlet: Type<T>;

    @Input() componentOutletInjector: Injector;

    @Input() componentOutletInputs: Record<string, unknown>;

    private inputs: Record<string, unknown>;

    componentRef: ComponentRef<T>;

    constructor(private viewContainerRef: ViewContainerRef) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.componentOutlet || changes.componentOutletInjector) {
            this.viewContainerRef.clear();

            if (this.componentOutlet) {
                const injector = this.componentOutletInjector || this.viewContainerRef.injector;
                const index = this.viewContainerRef.length;
                this.componentRef = this.viewContainerRef.createComponent(this.componentOutlet, { index, injector });
                this.inputs = Object.fromEntries((reflectComponentType(this.componentOutlet)?.inputs || []).map(i => [i.propName, true]));
            }
        }
        if (this.componentRef && changes.componentOutletInputs) {
            if (this.componentOutletInputs) {
                for (const [name, value] of Object.entries(this.componentOutletInputs)) {
                    if (this.inputs[name]) {
                        this.componentRef.setInput(name, value);
                    }
                }
                this.componentRef.changeDetectorRef.detectChanges();
            }
        }
    }

    writeValue(obj: unknown) {
        if (this.componentRef && isControlValueAccessor(this.componentRef.instance)) {
            this.componentRef.instance.writeValue(obj);
        }
    }

    registerOnChange(fn: unknown) {
        if (this.componentRef && isControlValueAccessor(this.componentRef.instance)) {
            this.componentRef.instance.registerOnChange(fn);
        }
    }

    registerOnTouched(fn: unknown): void {
        if (this.componentRef && isControlValueAccessor(this.componentRef.instance)) {
            this.componentRef.instance.registerOnTouched(fn);
        }
    }

    setDisabledState(isDisabled: boolean) {
        if (this.componentRef && hasIsDisabledState(this.componentRef.instance)) {
            this.componentRef.instance.setDisabledState(isDisabled);
        }
    }

    validate(c: AbstractControl) {
        if (this.componentRef && isValidator(this.componentRef.instance)) {
            return this.componentRef.instance.validate(c);
        }
        return null;
    }
}
